<template>
  <span
    class="rf-menu inline-flex items-center first-of-type:justify-center"
    :class="{ [typeClasses[props.variant]]: true }"
  >
    <RfButtonNew
      v-if="!props.anchor"
      ref="menuActivator"
      :tooltipPos="tooltipPos"
      :type="props.buttonType"
      :size="props.buttonSize"
      :disabled="props.disabled"
      class="rf-menu--button text-beige-50 inline-flex items-center [&_.rf-button--container]:w-full [&_.rf-button--container]:gap-1"
      :class="{ 'rf-menu--opened': modalOpened }"
      :aria-haspopup="ariaHaspopup"
      :aria-expanded="modalOpened"
      :aria-controls="modalOpened ? menuId : undefined"
      @click="disabled || (modalOpened ? hideMenu() : showMenu())"
      @keydown.esc="hideMenu"
    >
      <slot name="activator" :opened="modalOpened"></slot>
      <div v-if="!hideIcon && !disabled" class="rf-menu--icon-wrapper flex shrink-0">
        <RfChevronMiniClosedIcon
          class="shrink-0 transition-transform duration-200"
          :class="{
            'rotate-180': modalOpened,
            'h-6 w-6': ['base', 'none'].includes(props.buttonSize),
            'h-7 w-7': !['base', 'none'].includes(props.buttonSize),
          }"
        />
      </div>
    </RfButtonNew>
    <Teleport to="body">
      <Transition>
        <div
          v-if="modalOpened && !disabled"
          :id="menuId"
          ref="menu"
          :role="props.role || undefined"
          class="rf-menu--container [&.list,.list]:[&_>_a:hover,_>_button:hover]:bg-beige-1 list text-body bg-white-1 shadow-grey-1/25 text-grey-1 absolute top-0 left-0 box-border inline-flex w-max flex-col items-start overflow-y-auto overscroll-contain rounded"
          :style="{ zIndex }"
          @keydown.esc="hideMenu"
        >
          <slot name="menu" :close="hideMenu"></slot>
        </div>
      </Transition>
    </Teleport>
  </span>
</template>

<script>
const variants = {
  outlined: "OUTLINED",
  plain: "PLAIN",
};

const typeClasses = {
  plain: "rf-menu--plain",
  outlined:
    "rf-menu--outlined [&_.rf-menu--button]:bg-white-1 [&_.rf-menu--button]:min-h-9.5! [&_.rf-menu--button]:border-beige-10 [&_.rf-menu--button]:rounded-lg [&_.rf-menu--button]:border [&_.rf-menu--button]:border-solid [&_.rf-menu--button.disabled_.rf-icon]:fill-beige-20! [&_.rf-menu--button.disabled]:text-beige-20! [&_.rf-menu--button.disabled]:bg-white-1",
};
</script>

<script setup>
import RfChevronMiniClosedIcon from "@/components/icons/RfChevronMiniClosedIcon.vue";
import { onClickOutside } from "@vueuse/core";
import { computed, getCurrentInstance, nextTick, ref } from "vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import { useOverlayZIndex } from "@/utils/composables/useOverlayZIndex";
import { useFloatingUi } from "@/utils/composables/useFloatingUi";
import {
  FLOATING_UI_DEFAULT_PLACEMENT,
  FLOATING_UI_MENU_PRESETS,
} from "@/utils/constants/FloatingUiConstants";

const { zIndex } = useOverlayZIndex();
const { autoPosition, stopAutoPosition } = useFloatingUi();

const props = defineProps({
  anchor: { type: HTMLElement, default: null },
  preset: { type: String, default: null },
  placement: { type: String, default: null },
  hideIcon: { type: Boolean, default: false },
  disabled: { type: [Boolean, String], default: false },
  sameWidth: { type: Boolean, default: false },
  minFit: { type: Boolean, default: false },
  maxHeight: { type: String, default: null },
  buttonType: { type: String, default: "none" },
  buttonSize: { type: String, default: "none" },
  tooltipPos: { type: String, default: null },
  variant: {
    type: String,
    default: "plain",
    validator: v => Object.keys(variants).includes(v),
  },
  ariaHaspopup: { type: [Boolean, String], default: "true" },
  role: { type: String, default: null },
});

const emit = defineEmits(["hide", "show"]);

const { uid } = getCurrentInstance();
const menuId = `rf-menu-${uid}`;

const modalOpened = ref(false);
const stop = ref(() => {});
const menu = ref();
const menuActivator = ref();

const menuPreset = computed(
  () => FLOATING_UI_MENU_PRESETS[props.preset] ?? FLOATING_UI_MENU_PRESETS.default,
);

// Per-instance placement wins over the preset; presets without one fall back to the global default
const floatingOptions = computed(() => ({
  ...menuPreset.value,
  placement: props.placement || menuPreset.value.placement || FLOATING_UI_DEFAULT_PLACEMENT,
}));

const update = () => {
  if (props.disabled) return;
  nextTick(() => {
    const activatorValue =
      props.anchor ||
      (menuActivator.value?.$el || menuActivator.value)?.parentElement ||
      menuActivator.value;
    const menuValue = menu.value;

    Object.assign(menuValue.style, {
      ...(props.sameWidth && { width: `${activatorValue.clientWidth}px` }),
      ...(props.minFit && { minWidth: "fit-content" }),
    });

    autoPosition(activatorValue, menuValue, floatingOptions.value, () => {
      if (props.maxHeight) Object.assign(menuValue.style, { maxHeight: props.maxHeight });
    });
  });
};

const hideMenu = () => {
  stop.value?.();
  stopAutoPosition();
  modalOpened.value = false;
  emit("hide");
  (menuActivator.value?.$el || menuActivator.value)?.focus?.();
};

const showMenu = () => {
  if (props.disabled) return;
  modalOpened.value = true;
  update();

  nextTick(
    () =>
      (stop.value = onClickOutside(menu.value, hideMenu, {
        ignore: [
          props.anchor ||
            (menuActivator.value?.$el || menuActivator.value)?.parentElement ||
            menuActivator.value,
        ],
      })),
  );
  emit("show");
};

const toggleMenu = () => (modalOpened.value ? hideMenu() : showMenu());

defineExpose({ opened: modalOpened, show: showMenu, hide: hideMenu, toggle: toggleMenu, menuId });
</script>

<style lang="scss" scoped>
.rf-menu {
  &--container {
    box-shadow: 0px 4px 27px 0px var(--tw-shadow-color);
  }
}
</style>
